import { SubmissionParticipantForReception } from "@dune/types";
import { useMemo } from "react";
import {
	useGetConstructionSiteReceptionParticipants,
	useGetConstructionSiteSubmissionParticipants,
} from "../api/queries/constructionSiteSubmission.queries";

export const useReceptionParticipantsView = (
	constructionSiteId: number,
): {
	designers: SubmissionParticipantForReception[];
	builders: SubmissionParticipantForReception[];
	materials: SubmissionParticipantForReception[];
} => {
	const { data: receptionParticipants } =
		useGetConstructionSiteReceptionParticipants(constructionSiteId);
	const { data: submissionData } =
		useGetConstructionSiteSubmissionParticipants(constructionSiteId);

	return useMemo(() => {
		const result: {
			designers: SubmissionParticipantForReception[];
			builders: SubmissionParticipantForReception[];
			materials: SubmissionParticipantForReception[];
		} = { designers: [], builders: [], materials: [] };

		if (!receptionParticipants) return result;

		const submissionByParticipantId = new Map<
			number,
			{
				id: number;
				totalMissionsOrActivitiesOrMaterialTypes: string[];
			}
		>();
		if (submissionData?.participantToConstructionSiteSubmissions) {
			for (const sp of submissionData.participantToConstructionSiteSubmissions) {
				submissionByParticipantId.set(sp.participant.id, {
					id: sp.id,
					totalMissionsOrActivitiesOrMaterialTypes:
						sp.totalMissionsOrActivitiesOrMaterialTypes ?? [],
				});
			}
		}

		for (const {
			totalMissionsOrActivitiesOrMaterialTypes,
			participant,
			id,
			type,
			designerType,
			isFromSubmission,
			requiresLiftingReserves,
			totalFeesHT,
			totalFeesTTC,
		} of receptionParticipants) {
			const receptionActivities = totalMissionsOrActivitiesOrMaterialTypes;
			const submissionActivities =
				submissionByParticipantId.get(participant.id)
					?.totalMissionsOrActivitiesOrMaterialTypes ?? [];
			const participantInsurer = participant.insurers.at(0)?.insurerSociety;

			const enriched: SubmissionParticipantForReception = {
				id,
				type,
				designerType,
				totalFeesHT,
				totalFeesTTC,
				totalMissionsOrActivitiesOrMaterialTypes:
					receptionActivities && receptionActivities.length > 0
						? receptionActivities
						: submissionActivities,
				participant: {
					...participant,
					isNewForReception: !isFromSubmission,
				},
				decennialCertificatesOfSubmissionOrValidFromOthers: null,
				requiresLiftingReserves,
				insurer: participantInsurer
					? {
							id: participantInsurer.id,
							denomination: participantInsurer.society.denomination,
						}
					: null,
			};

			if (type === "DESIGNER") result.designers.push(enriched);
			else if (type === "BUILDER") result.builders.push(enriched);
			else if (type === "MATERIAL") result.materials.push(enriched);
		}

		return result;
	}, [
		receptionParticipants,
		submissionData?.participantToConstructionSiteSubmissions,
	]);
};
